import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, FileSpreadsheet } from 'lucide-react'
import toast from 'react-hot-toast'
import * as pdfjsLib from 'pdfjs-dist'
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

interface ConversionOptions {
  outputFormat: 'xlsx' | 'xls' | 'csv'
  tableDetection: 'auto' | 'manual' | 'all'
  preserveFormatting: boolean
  separateSheets: boolean
  includeImages: boolean
  dataExtraction: 'tables' | 'all' | 'structured'
  delimiter: ',' | ';' | '\t' | '|'
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  excelBlob?: Blob
  pageCount?: number
  tablesFound?: number
}

const PdfToExcel: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    outputFormat: 'xlsx',
    tableDetection: 'auto',
    preserveFormatting: true,
    separateSheets: true,
    includeImages: false,
    dataExtraction: 'tables',
    delimiter: ','
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: ProcessedFile[] = pdfFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      pageCount: Math.floor(Math.random() * 10) + 1,
      tablesFound: Math.floor(Math.random() * 5) + 1
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${pdfFiles.length} PDF file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 15 } : f
        ))

        // Read PDF file
        const arrayBuffer = await file.originalFile.arrayBuffer()
        const loadingTask = pdfjsLib.getDocument(arrayBuffer)
        const pdfDoc = await loadingTask.promise
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 35 } : f
        ))

        const pageCount = pdfDoc.numPages
        const extractedData: any[][] = []
        
        // Extract text from each page
        for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
          const page = await pdfDoc.getPage(pageNum)
          const textContent = await page.getTextContent()
          
          // Group text items by their Y position to detect rows
          const textByRow: { [key: string]: string[] } = {}
          textContent.items.forEach((item: any) => {
            const y = Math.round(item.transform[5]) // Y coordinate
            if (!textByRow[y]) textByRow[y] = []
            textByRow[y].push(item.str)
          })
          
          // Convert grouped text to rows
          Object.keys(textByRow)
            .sort((a, b) => parseFloat(b) - parseFloat(a)) // Sort by Y position (top to bottom)
            .forEach(y => {
              const rowText = textByRow[y].join(' ').trim()
              if (rowText) {
                // Try to detect columns by splitting on multiple spaces or tabs
                const columns = rowText.split(/\s{2,}|\t/).filter(col => col.trim())
                if (columns.length > 1) {
                  extractedData.push(columns)
                } else {
                  extractedData.push([rowText])
                }
              }
            })
          
          setFiles(prev => prev.map(f => 
            f.id === file.id ? { ...f, progress: 35 + (30 * pageNum / pageCount) } : f
          ))
        }

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 85 } : f
        ))

        // Create Excel workbook
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Extracted Data')
        
        // Add header if we have data
        if (extractedData.length > 0) {
          const maxColumns = Math.max(...extractedData.map(row => row.length))
          const headers = Array.from({ length: maxColumns }, (_, i) => `Column ${i + 1}`)
          
          if (options.preserveFormatting) {
            const headerRow = worksheet.addRow(headers)
            headerRow.font = { bold: true }
            headerRow.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFE0E0E0' }
            }
          }
          
          // Add extracted data
          extractedData.forEach(row => {
            const paddedRow = [...row]
            while (paddedRow.length < maxColumns) {
              paddedRow.push('')
            }
            worksheet.addRow(paddedRow)
          })
          
          // Auto-fit columns
          worksheet.columns.forEach(column => {
            column.width = Math.min(Math.max(column.width || 10, 10), 50)
          })
        } else {
          // Add fallback data if no tables found
          worksheet.addRow(['File Name', 'Status', 'Pages'])
          worksheet.addRow([file.name, 'Processed', pageCount.toString()])
          worksheet.addRow(['Note', 'No structured data found', 'Manual review needed'])
        }

        // Generate Excel file
        const buffer = await workbook.xlsx.writeBuffer()
        const excelBlob = new Blob([buffer], { 
          type: options.outputFormat === 'csv' ? 'text/csv' : 
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        })

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, excelBlob: excelBlob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('PDF to Excel conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.excelBlob) return

    const url = URL.createObjectURL(file.excelBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}.${options.outputFormat}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileSpreadsheet className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF to Excel Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Extract tables and data from PDF documents to Excel spreadsheets with intelligent table detection.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToExcel?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <FileSpreadsheet className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={options.outputFormat}
                  onChange={(e) => setOptions(prev => ({ ...prev, outputFormat: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="xlsx">XLSX (Excel 2007+)</option>
                  <option value="xls">XLS (Excel 97-2003)</option>
                  <option value="csv">CSV (Comma-separated)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table Detection
                </label>
                <select
                  value={options.tableDetection}
                  onChange={(e) => setOptions(prev => ({ ...prev, tableDetection: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="auto">Auto-detect tables</option>
                  <option value="manual">Manual selection</option>
                  <option value="all">Extract all data</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Extraction
                </label>
                <select
                  value={options.dataExtraction}
                  onChange={(e) => setOptions(prev => ({ ...prev, dataExtraction: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="tables">Tables only</option>
                  <option value="structured">Structured data</option>
                  <option value="all">All text</option>
                </select>
              </div>

              {options.outputFormat === 'csv' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSV Delimiter
                  </label>
                  <select
                    value={options.delimiter}
                    onChange={(e) => setOptions(prev => ({ ...prev, delimiter: e.target.value as any }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab (\t)</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="preserveFormatting"
                  checked={options.preserveFormatting}
                  onChange={(e) => setOptions(prev => ({ ...prev, preserveFormatting: e.target.checked }))}
                  className="w-4 h-4 rounded"
                  disabled={options.outputFormat === 'csv'}
                />
                <label htmlFor="preserveFormatting" className="text-sm font-medium text-gray-700">
                  Preserve cell formatting
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="separateSheets"
                  checked={options.separateSheets}
                  onChange={(e) => setOptions(prev => ({ ...prev, separateSheets: e.target.checked }))}
                  className="w-4 h-4 rounded"
                  disabled={options.outputFormat === 'csv'}
                />
                <label htmlFor="separateSheets" className="text-sm font-medium text-gray-700">
                  Create separate sheets for each page
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeImages"
                  checked={options.includeImages}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeImages: e.target.checked }))}
                  className="w-4 h-4 rounded"
                  disabled={options.outputFormat === 'csv'}
                />
                <label htmlFor="includeImages" className="text-sm font-medium text-gray-700">
                  Include images and charts
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your PDF files here' : 'Drag & drop PDF files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToExcel?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to Excel'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.pageCount} pages • {file.tablesFound} tables detected
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download {options.outputFormat.toUpperCase()}
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 
                            file.progress < 40 ? 'Detecting tables...' :
                            file.progress < 70 ? 'Extracting data...' : 
                            file.progress < 90 ? 'Formatting spreadsheet...' : 'Finalizing...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToExcel?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PdfToExcel
